/**
 * Ryan O'Dowd
 * 2023-05-29
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import CPassage from '@classes/Passage';
import LicensingInfo from '@components/LicensingInfo';
import OfflineBanner from '@components/common/OfflineBanner';
import {
  ScrollView,
} from 'react-native-gesture-handler';
import SelectionMenu from './SelectionMenu';
import Verse from './Verse';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import {
  useAnimatedRef,
} from 'react-native-reanimated';
import utilities from '@utils/utilities';

const PassageDisplay = (props: PassageDisplayProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const highlightedVerseRef: any = useAnimatedRef();
  const [_scrollToYPosition, setScrollToYPosition] = useState(0);

  const isAdmin = hooks.useIsAdmin();

  useEffect(() => {
    setTimeout(() => {
      highlightedVerseRef.current?.measure((__x, y) => {
        setScrollToYPosition(y);
      });
    }, 300);
  }, [highlightedVerseRef]);

  useEffect(() => {
    props.scrollRef?.current?.scrollTo({
      y: _scrollToYPosition,
      animated: true,
      // @TODO: would be nice to slow down the animation a 少し...but this requires using re-animated, and the juice isn't worth the squeeze right now for v1 of this feature
    });
  }, [props.scrollRef, highlightedVerseRef, _scrollToYPosition]);

  const translations = hooks.useTypedSelector((state) => {
    return state.translations.filter((t) => t.disabled === 0 && (t.downloadable === 1 || isAdmin)); // @TODO:  && t.locale_id === (state.oakUser.locale_id || RNLocalize.getLocales().map((lo) => lo.languageCode).filter((lc) => ['en', 'es'].includes(lc)).shift() || 'en'))); // @TODO: locale_id is now in user_settings, not on oakUser
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const [_selectedVerseNums, onVersePress, clearVerseSelection] = hooks.useSelectVerses([]);

  const book = hooks.useTypedSelector((state) => state.books.find((b) => b.id === props.passages[0].book_id)); // @TODO: only works because only one passage is passed in for PassageDisplays that allow for selection

  const showBadgesInRead = hooks.useTypedSelector((state) => state.settingsShowBadgesInRead);

  const isSelectionMenuOpen = props.alwaysShowSelectionMenu || !!_selectedVerseNums.length;

  useEffect(() => {
    if (props.onUpdateVerseSelection) {
      props.onUpdateVerseSelection(_selectedVerseNums);
    }

    if (props.onSelectionModeToggle) {
      props.onSelectionModeToggle(!!_selectedVerseNums.length);
    }
  }, [props, _selectedVerseNums]);

  const minSelectedVerseNum = Math.min(..._selectedVerseNums);
  const maxSelectedVerseNum = Math.max(..._selectedVerseNums);

  const allVerses = hooks.useGetVersesForPassages(props.passages); // @TODO: this doesn't get recalculated when Bible is deleted locally (edge case, but something to consider)
  const passagesVerses = [];
  props.passages.forEach((p) => {
    passagesVerses.push(...(allVerses[p.id] || []));
  });

  useEffect(() => {
    props.savePassageText?.(utilities.versesToText(Object.values(allVerses)[0] ?? []));
  }, [allVerses, props]);

  let passage = null;
  if (_selectedVerseNums.length) {
    passage = new CPassage(
      -1,
      props.passages[0].translationId, // @TODO: only works because only one passage is passed in for PassageDisplays that allow for selection
      book.id,
      props.passages[0].chapterNumber, // @TODO: only works because only one passage is passed in for PassageDisplays that allow for selection
      minSelectedVerseNum,
      maxSelectedVerseNum,
    );
  } else if (props.lessonAndTestAvailableWithoutSelection) {
    passage = props.passages[0]; // @TODO: only works because only one passage is passed in for PassageDisplays that allow for default lesson/test without selection
  }

  return (
    <View style={styles.container}>
      {isSelectionMenuOpen &&
        <SelectionMenu
          translations={translations}
          passage={passage}
          passageForContext={props.passageForContext}
          selectedVerseNums={_selectedVerseNums}
          clearSelection={() => clearVerseSelection()}
          hideCompare={props.hideCompare}
          hideAddToGoal={props.hideAddToGoal}
          hideShare={props.hideShare}
          hideTest={props.hideTest}
          hideLesson={props.hideLesson}
          hideReadInContext={props.hideReadInContext}
          hideNumbersTextToggle={props.hideNumbersTextToggle}
          showToggleBadges={props.showToggleBadges}
          spritzPassage={props.spritzPassage}
          spritzGoToPreviousPassage={props.spritzGoToPreviousPassage}
          spritzGoToNextPassage={props.spritzGoToNextPassage}
          isUnattachedToGoal={props.isUnattachedToGoal}
          bottomBorderColorOverride={props.bottomBorderColorOverride}
          assignmentId={props.assignmentId}
          referenceOnLineTwo={props.referenceOnLineTwo}
        />
      }
      {passagesVerses.some((verse) => verse.isLoading) && <OfflineBanner containerStyle={styles.offlineBanner} message={i18n('offlineVerses')} />}
      <ScrollView
        ref={props.scrollRef}
        contentContainerStyle={[
          styles.mainListContainer,
          props.hasSolidBackground && styles.verseWrapperBackgroundColor,
          props.isUnattachedToGoal && !props.hideLicensing && styles.extraBottomPadding, // @TODO: not a fan of hardcoding SelectionMenu's (approximate) height
          props.mainListContainerStyle,
        ]}
        style={props.mainListStyle}
        showsVerticalScrollIndicator={false}
      >
        <View style={props.verseNumbersOnly ? styles.numsOnlyVersesWrapper : styles.versesWrapper}>{/* @TODO: also have buttons for next/previous chapter...have persistent dash for this? */}
          {passagesVerses.map((verse) => {
            if (verse.rangeStartVerseNumber && !verse.verseText) { // @TODO: maybe also if !verse.verseText for empty verses? (make sure selection still works if empty verses are missing)
              return null;
            }

            const verseNum = +verse.verseApiId.split('.').pop();
            const isVerseSelected = _selectedVerseNums.includes(verseNum);
            const isVerseForContext = verseNum >= props.passageForContext?.startVerseNumber && verseNum <= props.passageForContext?.endVerseNumber;

            return ( // @TODO: would be nice if these verses wrapped, but worry about that at another time
              <View
                key={verse.verseApiId}
                ref={verseNum === props.passageForContext?.startVerseNumber ? highlightedVerseRef : null}
              >
                <Verse
                  isLoading={verse.isLoading}
                  // @TODO: verse={verse}
                  isSelected={isVerseSelected}
                  isForContext={isVerseForContext}
                  isSelectionModeOn={!!_selectedVerseNums.length}
                  verseApiId={verse.verseApiId}
                  rangeEndVerseNumber={verse.rangeEndVerseNumber}
                  translationId={verse.translationId}
                  showFormatting={props.showFormatting}
                  showFootnotes={props.showFootnotes}
                  showHeadings={props.showHeadings}
                  verseNumbersOnly={props.verseNumbersOnly}
                  textColorOverride={props.textColorOverride}
                  showWrinkleBadge={props.showWrinkleBadges || (props.showToggleBadges && showBadgesInRead)}
                  isBadgePressDisabled={props.isBadgePressDisabled}
                  onPressVerse={props.isPressDisabled ? null : () => onVersePress(verse)}
                  isPressDisabled={props.isPressDisabled}
                  skipFetch={true}// skip fetching of individual verses because this component (PassageDisplay) is already doing a fetch for the verse(s)
                  numsOnlyStyle={props.numsOnlyStyle}
                  verseTextStyles={props.verseTextStyles}
                  showContextHighlight={props.showContextHighlight}
                  hideProgressStyling={props.hideProgressStyling}
                />
              </View>
            );
          })}
        </View>
        {!props.hideLicensing &&
          <View style={styles.spacer} />
        }
        {!props.hideLicensing &&
          <View style={styles.licenseWrapper}>
            <LicensingInfo translationId={props.passages[0].translationId} />{/* @TODO: only works because licensing is displayed only for PassageDisplays that pass in a single passage */}
          </View>
        }
        {props.footerComponent}
      </ScrollView>
    </View>
  );
};

class PassageDisplayProps {
  passages: any[]; // @TODO: not sure if this should be passed or not
  showFormatting: boolean;
  showFootnotes: boolean;
  showHeadings: boolean;
  showWrinkleBadges: boolean;

  hideLicensing?: boolean;
  scrollRef?: any;
  hideCompare?: boolean;
  hideAddToGoal?: boolean;
  hideShare?: boolean;
  hideTest?: boolean;
  hideLesson?: boolean;
  hideNumbersTextToggle?: boolean;
  hideReadInContext?: boolean;
  showToggleBadges?: boolean;
  alwaysShowSelectionMenu?: boolean;
  isUnattachedToGoal?: boolean;
  isPressDisabled?: boolean;
  isBadgePressDisabled?: boolean;
  verseNumbersOnly?: boolean;
  onUpdateVerseSelection?: (verseNums: number[]) => void; // @TODO: verify this signature
  textColorOverride?: string;
  bottomBorderColorOverride?: string;
  onSelectionModeToggle?: (isOn: boolean) => void; // @TODO: verify signature
  mainListContainerStyle?: any;
  mainListStyle?: any;
  numsOnlyStyle?: any;
  verseTextStyles?: any;
  assignmentId?: number;
  hasSolidBackground?: boolean;
  passageForContext?: any;
  showContextHighlight?: boolean;
  referenceOnLineTwo?: boolean;
  lessonAndTestAvailableWithoutSelection?: boolean;
  spritzPassage?: any;
  spritzGoToPreviousPassage?: () => void; // @TODO: verify signature
  spritzGoToNextPassage?: () => void; // @TODO: verify signature
  hideProgressStyling?: boolean;
  savePassageText?: (verseText: string) => void;
  footerComponent?: React.ReactNode;
};

export default PassageDisplay;
